import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";

const passwordChangeSchema = z.object({
  newPassword: z.string(),
});

type PasswordChangeFormValues = z.infer<typeof passwordChangeSchema>;

interface AdminPasswordChangeFormProps {
  userId: string;
}

export function AdminPasswordChangeForm({
  userId,
}: AdminPasswordChangeFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      newPassword: "",
    },
  });

  const onSubmit = async (data: PasswordChangeFormValues) => {
    try {
      setIsLoading(true);
      await axiosInstance.post(
        `${process.env.REACT_APP_BASE_API_URL}auth/admin-change-password`,
        {
          userId: userId,
          newPassword: data.newPassword,
        },
      );
      toast({
        title: "Success",
        description: "Password changed successfully",
        variant: "success",
      });
      form.reset();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to change password",
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <h3 className="text-lg font-semibold">Admin: Change User Password</h3>
      <p className="text-sm text-muted-foreground">
        This form allows you to set a new password for the user, bypassing
        standard password requirements. Please ensure the new password is secure
        and communicate it to the user through a secure channel.
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New Password</FormLabel>
                <FormControl>
                  <Input type="password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Changing Password..." : "Change Password"}
          </Button>
        </form>
      </Form>
    </div>
  );
}
